import React from 'react';
import { 
  Icon, 
  CheckIcon, 
  DeleteIcon, 
  EditIcon, 
  PlusIcon, 
  MinusIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  WarningIcon,
  ErrorIcon,
  SuccessIcon,
  InformationIcon,
  ThreeDotsIcon,
  DownloadIcon,
  SettingsIcon,
  LogoutIcon,
  MenuIcon,
  HomeIcon,
  ProjectIcon,
  ExternalLinkIcon,
  EmailIcon,
  ChatIcon,
  HandPointerIcon,
  ProjectDiagramIcon,
  OpenFullIcon,
  ICON_SIZES,
  ICON_COLORS
} from './index';

const IconExample: React.FC = () => {
  const [isExpanded, setIsExpanded] = React.useState(false);

  return (
    <div style={{ padding: '20px', fontFamily: 'Arial, sans-serif' }}>
      <h1>Icon Library Examples</h1>
      
      <section>
        <h2>Basic Usage</h2>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '20px' }}>
          <Icon name="check" />
          <Icon name="delete" />
          <Icon name="edit" />
          <Icon name="plus" />
          <Icon name="minus" />
        </div>
      </section>

      <section>
        <h2>Pre-built Components</h2>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '20px' }}>
          <CheckIcon />
          <DeleteIcon />
          <EditIcon />
          <PlusIcon />
          <MinusIcon />
        </div>
      </section>

      <section>
        <h2>Different Sizes</h2>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '20px' }}>
          <Icon name="check" size="xs" />
          <Icon name="check" size="sm" />
          <Icon name="check" size="md" />
          <Icon name="check" size="lg" />
          <Icon name="check" size="xl" />
          <Icon name="check" size="2xl" />
        </div>
      </section>

      <section>
        <h2>Different Colors</h2>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '20px' }}>
          <Icon name="check" color="success" />
          <Icon name="warning" color="warning" />
          <Icon name="error" color="danger" />
          <Icon name="information" color="info" />
          <Icon name="check" color="primary" />
          <Icon name="check" color="secondary" />
        </div>
      </section>

      <section>
        <h2>Status Icons</h2>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '20px' }}>
          <SuccessIcon size="lg" color="success" />
          <WarningIcon size="lg" color="warning" />
          <ErrorIcon size="lg" color="danger" />
          <InformationIcon size="lg" color="info" />
        </div>
      </section>

      <section>
        <h2>Navigation Icons</h2>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '20px' }}>
          <HomeIcon />
          <ProjectIcon />
          <ExternalLinkIcon />
          <div 
            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
            <span style={{ marginLeft: '5px' }}>Toggle Section</span>
          </div>
        </div>
      </section>

      <section>
        <h2>Action Icons</h2>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '20px' }}>
          <button style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <PlusIcon size="sm" />
            Add Item
          </button>
          <button style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <EditIcon size="sm" />
            Edit
          </button>
          <button style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <DeleteIcon size="sm" color="danger" />
            Delete
          </button>
          <button style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
            <DownloadIcon size="sm" />
            Download
          </button>
        </div>
      </section>

      <section>
        <h2>UI Icons</h2>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '20px' }}>
          <ThreeDotsIcon />
          <SettingsIcon />
          <LogoutIcon />
          <MenuIcon />
          <EmailIcon />
          <ChatIcon />
          <HandPointerIcon />
          <ProjectDiagramIcon />
          <OpenFullIcon />
        </div>
      </section>

      <section>
        <h2>Custom Styling</h2>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '20px' }}>
          <Icon name="check" size={28} color="#ff6b6b" />
          <Icon name="check" size={28} color="#4ecdc4" />
          <Icon name="check" size={28} color="#45b7d1" />
          <Icon name="check" size={28} color="#96ceb4" />
          <Icon name="check" size={28} color="#feca57" />
        </div>
      </section>

      <section>
        <h2>Available Sizes</h2>
        <div style={{ marginBottom: '20px' }}>
          {Object.entries(ICON_SIZES).map(([size, pixels]) => (
            <div key={size} style={{ marginBottom: '5px' }}>
              <strong>{size}:</strong> {pixels}px
            </div>
          ))}
        </div>
      </section>

      <section>
        <h2>Available Colors</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px', marginBottom: '20px' }}>
          {Object.entries(ICON_COLORS).map(([color, hex]) => (
            <div key={color} style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
              <Icon name="check" size="sm" color={color as any} />
              <span><strong>{color}:</strong> {hex}</span>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default IconExample; 